import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { placeName } from '../locations';
import api from '../config/api';
import { uiLang, useKinds } from '../kinds';
import { errorMessage } from '../errors';
import { notifyChanged } from '../shell/changes';
import { Alert, Button, Dialog, Select, Skeleton, TextField, Textarea } from '../ui';
import { PlusIcon } from '../ui/icons';

const PREVIEW_FIELDS = 8;
const PREVIEW_PLACES = 8;

/**
 * "New collection": name, description, the item types it holds (one is the
 * default) and, optionally, a location type. Only the caller's own types
 * can be used. A model the caller has no type of yet (Film, Book…) can be
 * started from here: the type is created from it, named after it, and
 * ticked (phase 4: "Nouvelle collection" offers the Livre model).
 */
function CreateInventoryModal({ onClose, onCreate }) {
  const { t, i18n } = useTranslation();
  const lang = uiLang(i18n);
  const kinds = useKinds();
  const [creatingModel, setCreatingModel] = useState(null);
  const [modelError, setModelError] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    location_type_id: ''
  });

  const [itemTypes, setItemTypes] = useState([]);
  const [selectedItemTypes, setSelectedItemTypes] = useState([]);
  const [defaultItemTypeId, setDefaultItemTypeId] = useState('');
  const [locationTypes, setLocationTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchItemTypes();
    fetchLocationTypes();
  }, []);

  const fetchItemTypes = async () => {
    try {
      const response = await api.get('/item-types');
      // An inventory can only use its creator's own types
      setItemTypes(response.data.filter(type => type.is_mine));
    } catch (error) {
      console.error('Error fetching item types:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLocationTypes = async () => {
    try {
      const response = await api.get('/location-types');
      setLocationTypes(response.data.filter(type => type.is_mine));
    } catch (error) {
      console.error('Error fetching location types:', error);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleItemTypeToggle = (itemTypeId) => {
    if (selectedItemTypes.includes(itemTypeId)) {
      const newSelected = selectedItemTypes.filter(id => id !== itemTypeId);
      setSelectedItemTypes(newSelected);
      // The default removed: the next selected one becomes the default.
      if (defaultItemTypeId === itemTypeId) {
        setDefaultItemTypeId(newSelected.length > 0 ? newSelected[0] : '');
      }
    } else {
      const newSelected = [...selectedItemTypes, itemTypeId];
      setSelectedItemTypes(newSelected);
      // The first one selected is the default.
      if (newSelected.length === 1) {
        setDefaultItemTypeId(itemTypeId);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || selectedItemTypes.length === 0) return;
    setSubmitting(true);
    try {
      await onCreate({
        ...formData,
        item_types: selectedItemTypes,
        default_item_type_id: defaultItemTypeId
      });
    } finally {
      setSubmitting(false);
    }
  };

  // The models the caller has no type of yet.
  const models = kinds.flatMap(kind => kind.presets).filter(preset => !itemTypes.some(type => type.preset === preset.id));
  const startFromModel = async (preset) => {
    setCreatingModel(preset.id);
    setModelError(null);
    try {
      const response = await api.post('/item-types', { from_preset: preset.id, name: preset.labels[lang], lang });
      const type = response.data;
      setItemTypes(prev => [...prev, { ...type, is_mine: true }]);
      setSelectedItemTypes(prev => [...prev, type.id]);
      setDefaultItemTypeId(prev => prev || type.id);
      notifyChanged('item-types');
    } catch (error) {
      setModelError(t('createCollection.modelFailed', { error: errorMessage(t, error) }));
    } finally {
      setCreatingModel(null);
    }
  };

  const defaultItemType = itemTypes.find(type => type.id === parseInt(defaultItemTypeId, 10));
  const selectedLocationType = locationTypes.find(type => type.id === parseInt(formData.location_type_id, 10));

  if (!loading && itemTypes.length === 0 && models.length === 0) {
    return (
      <Dialog
        open
        onClose={onClose}
        title={t('createCollection.noTypesTitle')}
        description={t('createCollection.noTypesText')}
        size="sm"
        footer={(
          <>
            <Button variant="secondary" onClick={onClose}>{t('common.close')}</Button>
            <Button as={Link} to="/settings/item-types" onClick={onClose}>{t('createCollection.toItemTypes')}</Button>
          </>
        )}
      />
    );
  }

  const canSubmit = formData.name.trim() && selectedItemTypes.length > 0;

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('createCollection.title')}
      size="lg"
      footer={(
        <>
          <Button variant="secondary" onClick={onClose}>{t('common.cancel')}</Button>
          <Button type="submit" form="create-collection" disabled={!canSubmit} loading={submitting}>{t('createCollection.submit')}</Button>
        </>
      )}
    >
      <form id="create-collection" className="dialog-form" onSubmit={handleSubmit}>
        <TextField
          label={t('createCollection.name')}
          name="name"
          value={formData.name}
          onChange={handleInputChange}
          required
          placeholder={t('createCollection.namePlaceholder')}
        />
        <Textarea
          label={t('createCollection.description')}
          name="description"
          value={formData.description}
          onChange={handleInputChange}
          rows={2}
          placeholder={t('createCollection.descriptionPlaceholder')}
        />

        <fieldset className="choice-list">
          <legend className="ui-field__label">{t('createCollection.itemTypes')}</legend>
          {loading ? (
            <div className="choice-list__rows">
              {[0, 1, 2].map(i => <Skeleton key={i} height={52} />)}
            </div>
          ) : (
            <ul className="choice-list__rows">
              {itemTypes.map(itemType => {
                const selected = selectedItemTypes.includes(itemType.id);
                return (
                  <li key={itemType.id} className={`choice-row${selected ? ' is-selected' : ''}`}>
                    <input
                      type="checkbox"
                      id={`itemtype-${itemType.id}`}
                      checked={selected}
                      onChange={() => handleItemTypeToggle(itemType.id)}
                    />
                    <label htmlFor={`itemtype-${itemType.id}`} className="choice-row__text">
                      <span className="choice-row__title">{itemType.name}</span>
                      {itemType.description && <span className="choice-row__hint">{itemType.description}</span>}
                      <span className="choice-row__hint">{t('collections.fieldCount', { count: itemType.fields?.length || 0 })}</span>
                    </label>
                    {selected && (
                      <label className="choice-row__default">
                        <input
                          type="radio"
                          name="defaultItemType"
                          checked={defaultItemTypeId === itemType.id}
                          onChange={() => setDefaultItemTypeId(itemType.id)}
                        />
                        {t('collections.default')}
                      </label>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
          {!loading && models.length > 0 && (
            <div className="model-shortcuts">
              <p className="ui-field__hint">{itemTypes.length === 0 ? t('createCollection.noTypesYet') : t('createCollection.orFromModel')}</p>
              <div className="model-shortcuts__list">
                {models.map(preset => (
                  <Button key={preset.id} variant="secondary" size="sm" icon={PlusIcon} loading={creatingModel === preset.id}
                          disabled={creatingModel !== null} onClick={() => startFromModel(preset)}>
                    {preset.labels[lang]}
                  </Button>
                ))}
              </div>
              {modelError && <Alert tone="danger">{modelError}</Alert>}
            </div>
          )}
          <p className={`ui-field__hint${!loading && selectedItemTypes.length === 0 ? ' is-warning' : ''}`} aria-live="polite">
            {selectedItemTypes.length === 0
              ? t('createCollection.selectOne')
              : [t('createCollection.selected', { count: selectedItemTypes.length }),
                defaultItemType ? t('createCollection.defaultIs', { name: defaultItemType.name }) : null].filter(Boolean).join(' · ')}
          </p>
        </fieldset>

        {defaultItemType && (defaultItemType.fields || []).length > 0 && (
          <div className="dialog-preview">
            <p className="dialog-preview__title">{t('createCollection.fieldsByDefault')}</p>
            <ul className="tag-list">
              {defaultItemType.fields.slice(0, PREVIEW_FIELDS).map(field => (
                <li key={field.key || field.name} className="ui-chip ui-chip--static">{field.name}</li>
              ))}
              {defaultItemType.fields.length > PREVIEW_FIELDS && (
                <li className="tag-list__more">{t('createCollection.moreFields', { count: defaultItemType.fields.length - PREVIEW_FIELDS })}</li>
              )}
            </ul>
          </div>
        )}

        <Select
          label={t('createCollection.locationType')}
          name="location_type_id"
          value={formData.location_type_id}
          onChange={handleInputChange}
          hint={t('createCollection.locationHint')}
        >
          <option value="">{t('createCollection.noLocation')}</option>
          {locationTypes.map(locationType => (
            <option key={locationType.id} value={locationType.id}>
              {locationType.name} ({t('collections.locationCount', { count: locationType.location_count ?? 0 })})
            </option>
          ))}
        </Select>

        {selectedLocationType && (
          <div className="dialog-preview">
            <p className="dialog-preview__title">{t('createCollection.locationOptions')}</p>
            {selectedLocationType.places && selectedLocationType.places.length > 0 ? (
              <ul className="tag-list">
                {selectedLocationType.places.slice(0, PREVIEW_PLACES).map(place => (
                  <li key={place.id} className="ui-chip ui-chip--static">{placeName(t, place)}</li>
                ))}
                {selectedLocationType.places.length > PREVIEW_PLACES && (
                  <li className="tag-list__more">{t('createCollection.moreLocations', { count: selectedLocationType.places.length - PREVIEW_PLACES })}</li>
                )}
              </ul>
            ) : (
              <p className="ui-field__hint">{t('createCollection.noLocations')}</p>
            )}
          </div>
        )}
      </form>
    </Dialog>
  );
}

export default CreateInventoryModal;
